import type { ClientCapability } from '@shared/types.ts';

/**
 * The native player, when there is one.
 *
 * `window.N8MediaNative` is injected by the Android wrapper. Its EXISTENCE is
 * the capability check — there is deliberately no "am I on a TV?" test here,
 * because the question that matters is not what device this is but what it can
 * decode. A browser, an iPhone and the LG TV simply do not have this object
 * and take the hls.js path unchanged.
 *
 * See docs/FIELD-NOTES.md §9d for why native playback exists at all.
 */

export interface NativeBridge {
  play(url: string, mimeType: string): void;
  /**
   * Start part-way in. Absent on wrappers built before library playback, so
   * every call site has to check — an older APK must resume from the start
   * rather than throw.
   */
  playFrom?(url: string, mimeType: string, startPositionMs: number): void;
  pause(): void;
  resume(): void;
  stop(): void;
  setVolume(v: number): void;
  setRect(left: number, top: number, width: number, height: number, cssWidth: number): void;
  setBackground(css: string): void;
  capabilities(): string;
  /** Milliseconds behind the live edge, or 0 when the player cannot say. */
  latencyMs(): number;
  /*
   * SEEKING, added after the fact — so every one of these is OPTIONAL and
   * every call site has to check.
   *
   * A television running last month's APK has a bridge without them, and the
   * web app is updated by a page refresh while the APK is updated by somebody
   * walking over with a remote. An app that assumed the newest wrapper would
   * throw on the oldest television in the house.
   */
  /** The build of the wrapper this page is running inside. */
  appVersionCode?(): number;
  seekBy?(deltaMs: number): void;
  seekTo?(positionMs: number): void;
  positionMs?(): number;
  durationMs?(): number;
  /** ExoPlayer LoadControl durations and byte ceiling; applies on next play(). */
  setBufferProfile(
    minMs: number, maxMs: number, forPlaybackMs: number, afterRebufferMs: number,
    targetBytes: number,
  ): void;
}

export type NativeStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

export function nativeBridge(): NativeBridge | null {
  const b = (window as any).N8MediaNative as NativeBridge | undefined;
  return b && typeof b.play === 'function' ? b : null;
}

/**
 * Is this a television?
 *
 * The APK's bootstrap stamps `html.tv`, so this is true in the living room
 * and false in a browser — including a browser on the same machine. It
 * decides things a screen size cannot: whether there is a pointer, whether
 * there is a keyboard, and whether the person is ten feet away.
 */
export function isTv(): boolean {
  return document.documentElement.classList.contains('tv');
}

export function hasNative(): boolean {
  return nativeBridge() !== null;
}

/** What we tell the server we can decode, as `?can=`. */
export function clientCapabilities(): ClientCapability[] {
  const b = nativeBridge();
  if (!b) return [];
  try {
    return b.capabilities().split(',').map((c) => c.trim()).filter(Boolean) as ClientCapability[];
  } catch {
    return [];
  }
}

type StatusListener = (status: NativeStatus, error?: string) => void;
const listeners = new Set<StatusListener>();

/**
 * The wrapper calls this on every playback state change. Installed once, at
 * module load, so a state change that arrives before React has mounted
 * anything is still delivered rather than dropped.
 */
(window as any).__n8mNativeEvent = (msg: { t: string; status: NativeStatus; error?: string }) => {
  if (msg?.t !== 'state') return;
  for (const l of listeners) l(msg.status, msg.error);
};

export function onNativeStatus(fn: StatusListener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Keep the native surface on top of wherever the page put its <video>.
 *
 * The page keeps owning layout — the invisible <video> element is still
 * positioned by exactly the same CSS as before, corner PiP in browse and
 * full-bleed in theater — and the native layer follows it. That is what lets
 * the three-tier presentation model survive a change of decoder without a
 * single rule of CSS moving.
 *
 * Coordinates go over in CSS pixels along with the page's own width, because
 * on a TV the viewport is pinned to 1280 whatever the panel is; the wrapper
 * scales them. Neither side has to know the device's pixel density.
 */
export function trackVideoRect(el: HTMLElement): () => void {
  const bridge = nativeBridge();
  if (!bridge) return () => { /* nothing to track */ };

  let raf = 0;
  const push = () => {
    raf = 0;
    const r = el.getBoundingClientRect();

    /*
     * A DEGENERATE RECT IS NOT A SIZE, it is a measurement taken at a bad
     * moment — a suspended WebView, a route mid-swap, a layout that has not
     * settled. Sending it shrinks the video surface to nothing, and since the
     * surface only moves when something tells it to, the picture stays gone
     * even after the page recovers. That is a blank screen with playback
     * still running, which is exactly what it looks like from the sofa.
     *
     * Keeping the last good rectangle costs a frame of staleness at worst.
     */
    if (r.width < 1 || r.height < 1) return;

    try {
      bridge.setRect(r.left, r.top, r.width, r.height, window.innerWidth);
    } catch { /* the bridge went away with the page */ }
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(push); };

  // A ResizeObserver alone misses a MOVE with no size change — exactly what
  // stepping from theater back to the corner does at some window sizes.
  const ro = new ResizeObserver(schedule);
  ro.observe(el);
  const mo = new MutationObserver(schedule);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  mo.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
  window.addEventListener('resize', schedule);
  window.addEventListener('transitionend', schedule);
  // Coming back from a suspended WebView: the layout is re-settling and the
  // surface has to be told where the picture goes again.
  document.addEventListener('visibilitychange', schedule);

  schedule();
  return () => {
    if (raf) cancelAnimationFrame(raf);
    ro.disconnect();
    mo.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener('transitionend', schedule);
    document.removeEventListener('visibilitychange', schedule);
  };
}

/**
 * Hand the page's background colour to the wrapper.
 *
 * The WebView is transparent so the video can show through it, which makes
 * the view BEHIND it the app's background — so it has to follow the theme the
 * user picked, or every screen has the wrong backdrop.
 */
export function pushBackgroundColor(): void {
  const bridge = nativeBridge();
  if (!bridge) return;
  try {
    bridge.setBackground(getComputedStyle(document.body).backgroundColor || '#000');
  } catch { /* not fatal — the wrapper keeps its last colour */ }
}
